import { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { G } from "@/brand";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { CheckCircle2 } from "lucide-react";

export default function Success() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const { setUser, user, refresh } = useAuth();
  const [state, setState] = useState("polling"); // polling|done|failed
  const [info, setInfo] = useState(null);
  const attempts = useRef(0);
  const isDonation = params.get("kind") === "donation";

  useEffect(() => {
    const sid = params.get("session_id");
    if (!sid) { setState("failed"); return; }

    let cancelled = false;
    const poll = async () => {
      if (cancelled) return;
      attempts.current += 1;
      try {
        const path = isDonation ? `/donate/status/${sid}` : `/checkout/status/${sid}`;
        const { data } = await api.get(path);
        if (data.payment_status === "paid") {
          setInfo(data);
          if (!isDonation) {
            if (user) setUser({ ...user, access_tier: data.access_tier, credit_balance: data.credit_balance });
            else refresh();
          }
          setState("done");
          return;
        }
        if (data.status === "expired") { setState("failed"); return; }
      } catch (e) { if (process.env.NODE_ENV === "development") console.debug("Payment status poll failed, retrying:", e?.message); }
      if (attempts.current >= 8) { setState("failed"); return; }
      setTimeout(poll, 2000);
    };
    poll();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div style={{ background: G.black, minHeight: "100vh", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 18, padding: 24, textAlign: "center" }}>
      {state === "polling" && (<><div className="spinner" /><div className="mono" style={{ color: G.gold, fontSize: 11, letterSpacing: ".2em" }}>CONFIRMING PAYMENT…</div></>)}
      {state === "done" && isDonation && (
        <div data-testid="donation-thanks" style={{ maxWidth: 460 }}>
          <CheckCircle2 size={48} color={G.gold} style={{ margin: "0 auto 18px" }} />
          <div style={{ fontSize: 34, color: "#fff", fontStyle: "italic", marginBottom: 12 }}>Thank you ✦</div>
          <div style={{ color: G.muted, fontSize: 16, fontStyle: "italic", marginBottom: 24 }}>
            Your <b style={{ color: G.gold }}>${info?.amount}</b> tip keeps Prompt Savant free and funds new genres and prompts. You're the reason this exists.
          </div>
          <button onClick={() => nav("/library")} className="btn-gold" style={{ padding: "13px 26px", fontSize: 10 }}>Back to Library</button>
        </div>
      )}
      {state === "done" && !isDonation && (
        <div data-testid="success-card" style={{ maxWidth: 460 }}>
          <CheckCircle2 size={48} color={G.gold} style={{ margin: "0 auto 18px" }} />
          <div style={{ fontSize: 34, color: "#fff", fontStyle: "italic", marginBottom: 12 }}>You're unlocked.</div>
          <div style={{ color: G.muted, fontSize: 16, fontStyle: "italic", marginBottom: 24 }}>
            Tier upgraded to <b style={{ color: G.gold }}>{info?.access_tier === "upgrade_2" ? "Codex" : "Vault"}</b>. Credits now: <b style={{ color: G.gold }}>{info?.credit_balance}</b>.
          </div>
          <div style={{ display: "flex", gap: 12, justifyContent: "center" }}>
            <button data-testid="success-library" onClick={() => nav("/library")} className="btn-gold" style={{ padding: "13px 26px", fontSize: 10 }}>Open Library</button>
            <button onClick={() => nav("/dashboard")} className="btn-outline" style={{ padding: "13px 26px", fontSize: 10 }}>Dashboard</button>
          </div>
        </div>
      )}
      {state === "failed" && (
        <div style={{ maxWidth: 440 }}>
          <div style={{ fontSize: 26, color: "#fff", fontStyle: "italic", marginBottom: 16 }}>Payment not confirmed yet</div>
          <div style={{ color: G.muted, fontStyle: "italic", marginBottom: 22 }}>If you completed payment, your access updates shortly. Otherwise try again.</div>
          <button onClick={() => nav("/pricing")} className="btn-gold" style={{ padding: "13px 26px", fontSize: 10 }}>Back to Pricing</button>
        </div>
      )}
    </div>
  );
}
